import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { authApi } from '../../services/api/auth.api';
import { getApiErrorCode, getApiErrorMessage } from '../../utils/apiError';

const MIN_PASSWORD_LENGTH = 8;
const INVALID_LINK_MESSAGE = 'Ce lien n’est plus valide. Demandez un nouveau lien à votre administrateur.';

/** Compte créé sans email : le backend stocke un placeholder à ne pas afficher. */
const PLACEHOLDER_EMAIL_SUFFIX = '@a-completer.invalid';

const inputClass =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-slate-400';

export const AdminSetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [info, setInfo] = useState<{ displayName: string; email: string } | null>(null);
  const [invalid, setInvalid] = useState(!token);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    authApi
      .getPasswordSetup(token)
      .then(setInfo)
      .catch(() => setInvalid(true));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`);
      return;
    }
    if (password !== confirmation) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await authApi.completePasswordSetup(token, password);
      localStorage.setItem('teamlab_admin_token', result.token);
      navigate('/admin', { replace: true });
    } catch (err) {
      if (getApiErrorCode(err) === 'INVALID_SETUP_LINK') {
        setInvalid(true);
      } else {
        setError(getApiErrorMessage(err, 'Impossible d’enregistrer le mot de passe.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-slate-200 bg-white/90 backdrop-blur-xl shadow-xl shadow-slate-200/60 p-8">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg">
            <KeyRound className="h-7 w-7" />
          </div>
          {invalid ? (
            <h1 className="text-2xl font-black tracking-tight text-slate-900">Lien expiré</h1>
          ) : info ? (
            <>
              <h1 className="text-2xl font-black tracking-tight text-slate-900">Bienvenue {info.displayName}</h1>
              <p className="mt-2 text-sm text-slate-500">
                Choisissez votre mot de passe
                {!info.email.endsWith(PLACEHOLDER_EMAIL_SUFFIX) && (
                  <>
                    {' '}pour <span className="font-semibold text-slate-700">{info.email}</span>
                  </>
                )}
              </p>
            </>
          ) : (
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
          )}
        </div>

        {invalid ? (
          <div className="space-y-5">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
              {INVALID_LINK_MESSAGE}
            </div>
            <Link
              to="/admin/login"
              className="block w-full rounded-2xl bg-slate-900 px-4 py-3 text-center text-sm font-bold text-white transition hover:bg-slate-800"
            >
              Aller à la connexion
            </Link>
          </div>
        ) : info ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                placeholder={`${MIN_PASSWORD_LENGTH} caractères minimum`}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                autoFocus
                required
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">Confirmation</label>
              <input
                type="password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                className={inputClass}
                placeholder="Retapez le mot de passe"
                autoComplete="new-password"
                required
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Enregistrement…' : 'Enregistrer et se connecter'}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
};
